/**
 * Centralized validation constants - Single Source of Truth (SSOT)
 * These constants match database field constraints exactly.
 * 
 * Database schema constraints:
 * - Standard name fields: varchar(255) - employer, position, institution names, etc.
 * - Location fields: varchar(100) - city, country
 * - Personal names: varchar(100) - first_name, last_name
 * - Skill/Language names: varchar(100)
 * - Description fields: varchar(1000-5000)
 */

// Base validation values - Single Source of Truth
export const VALIDATION_VALUES = {
  // Standard field lengths
  CITY: 100,
  COUNTRY: 100,
  STANDARD_NAME: 255,        // employer, position, institution names, titles
  PERSONAL_NAME: 100,        // first_name, last_name
  SKILL_LANGUAGE_NAME: 100,  // skill names, language names
  SHORT_TEXT: 50,            // titles, levels, zip codes, phone
  DESCRIPTION: 1000,         // standard descriptions
  LONG_DESCRIPTION: 5000,    // detailed descriptions
  
  // Date constraints
  MIN_DATE: '1900-01-01',
  MAX_DATE: '2100-12-31',
  
  // Numeric constraints
  DURATION_MIN: 1,
  DURATION_MAX: 999,
} as const;

// Specific field validations using the base values
export const FIELD_VALIDATION = {
  // Location fields (used everywhere)
  CITY_MAX_LENGTH: VALIDATION_VALUES.CITY,
  COUNTRY_MAX_LENGTH: VALIDATION_VALUES.COUNTRY,
  
  // Work Experience
  EMPLOYER_MAX_LENGTH: VALIDATION_VALUES.STANDARD_NAME,
  POSITION_MAX_LENGTH: VALIDATION_VALUES.STANDARD_NAME,
  WORK_EXPERIENCE_DESCRIPTION_MAX_LENGTH: VALIDATION_VALUES.LONG_DESCRIPTION,
  
  // Education  
  UNIVERSITY_NAME_MAX_LENGTH: VALIDATION_VALUES.STANDARD_NAME,
  HIGH_SCHOOL_NAME_MAX_LENGTH: VALIDATION_VALUES.STANDARD_NAME,
  FIELD_OF_STUDY_MAX_LENGTH: VALIDATION_VALUES.STANDARD_NAME,
  
  // Personal Data
  FIRST_NAME_MAX_LENGTH: VALIDATION_VALUES.PERSONAL_NAME,
  LAST_NAME_MAX_LENGTH: VALIDATION_VALUES.PERSONAL_NAME,
  STREET_NAME_MAX_LENGTH: VALIDATION_VALUES.STANDARD_NAME,
  STREET_NUMBER_MAX_LENGTH: VALIDATION_VALUES.SHORT_TEXT,
  ZIP_MAX_LENGTH: VALIDATION_VALUES.SHORT_TEXT,
  TELEPHONE_MAX_LENGTH: VALIDATION_VALUES.SHORT_TEXT,
  
  // Skills & Languages
  SKILL_NAME_MAX_LENGTH: VALIDATION_VALUES.SKILL_LANGUAGE_NAME,
  LANGUAGE_NAME_MAX_LENGTH: VALIDATION_VALUES.SKILL_LANGUAGE_NAME,
  
  // Titles and Short Fields
  TITLE_MAX_LENGTH: VALIDATION_VALUES.SHORT_TEXT,
  TITLE_AFTER_NAME_MAX_LENGTH: VALIDATION_VALUES.SHORT_TEXT,
  
  // Projects
  PROJECT_TITLE_MAX_LENGTH: VALIDATION_VALUES.STANDARD_NAME,
  
  // Descriptions
  DESCRIPTION_MAX_LENGTH: VALIDATION_VALUES.DESCRIPTION,
  LONG_DESCRIPTION_MAX_LENGTH: VALIDATION_VALUES.LONG_DESCRIPTION,
} as const;

// Legacy exports for backward compatibility (will be deprecated)
export const WORK_EXPERIENCE_VALIDATION = {
  EMPLOYER_MAX_LENGTH: FIELD_VALIDATION.EMPLOYER_MAX_LENGTH,
  POSITION_MAX_LENGTH: FIELD_VALIDATION.POSITION_MAX_LENGTH,
  CITY_MAX_LENGTH: FIELD_VALIDATION.CITY_MAX_LENGTH,
  COUNTRY_MAX_LENGTH: FIELD_VALIDATION.COUNTRY_MAX_LENGTH,
  DESCRIPTION_MAX_LENGTH: FIELD_VALIDATION.WORK_EXPERIENCE_DESCRIPTION_MAX_LENGTH,
} as const;

export const EDUCATION_VALIDATION = {
  HIGH_SCHOOL_NAME_MAX_LENGTH: FIELD_VALIDATION.HIGH_SCHOOL_NAME_MAX_LENGTH,
  HIGH_SCHOOL_FIELD_OF_STUDY_MAX_LENGTH: FIELD_VALIDATION.FIELD_OF_STUDY_MAX_LENGTH,
  HIGH_SCHOOL_CITY_MAX_LENGTH: FIELD_VALIDATION.CITY_MAX_LENGTH,
  HIGH_SCHOOL_COUNTRY_MAX_LENGTH: FIELD_VALIDATION.COUNTRY_MAX_LENGTH,
  UNIVERSITY_NAME_MAX_LENGTH: FIELD_VALIDATION.UNIVERSITY_NAME_MAX_LENGTH,
  UNIVERSITY_FIELD_OF_STUDY_MAX_LENGTH: FIELD_VALIDATION.FIELD_OF_STUDY_MAX_LENGTH,
  UNIVERSITY_CITY_MAX_LENGTH: FIELD_VALIDATION.CITY_MAX_LENGTH,
  UNIVERSITY_COUNTRY_MAX_LENGTH: FIELD_VALIDATION.COUNTRY_MAX_LENGTH,
} as const;

export const SKILLS_LANGUAGES_VALIDATION = {
  SKILL_NAME_MAX_LENGTH: FIELD_VALIDATION.SKILL_NAME_MAX_LENGTH,
  LANGUAGE_NAME_MAX_LENGTH: FIELD_VALIDATION.LANGUAGE_NAME_MAX_LENGTH,
} as const;

export const PROJECT_VALIDATION = {
  TITLE_MAX_LENGTH: VALIDATION_VALUES.STANDARD_NAME,  // 255 chars for project title
  POSITION_MAX_LENGTH: VALIDATION_VALUES.STANDARD_NAME,  // 255 chars for position
  COUNTRY_MAX_LENGTH: FIELD_VALIDATION.COUNTRY_MAX_LENGTH,
  MIN_DURATION: 0,
} as const;

export const PERSONAL_DATA_VALIDATION = {
  FIRST_NAME_MAX_LENGTH: FIELD_VALIDATION.FIRST_NAME_MAX_LENGTH,
  LAST_NAME_MAX_LENGTH: FIELD_VALIDATION.LAST_NAME_MAX_LENGTH,
  TITLE_MAX_LENGTH: FIELD_VALIDATION.TITLE_MAX_LENGTH,
  TITLE_AFTER_NAME_MAX_LENGTH: FIELD_VALIDATION.TITLE_AFTER_NAME_MAX_LENGTH,
  STREET_NAME_MAX_LENGTH: FIELD_VALIDATION.STREET_NAME_MAX_LENGTH,
  STREET_NUMBER_MAX_LENGTH: FIELD_VALIDATION.STREET_NUMBER_MAX_LENGTH,
  CITY_MAX_LENGTH: FIELD_VALIDATION.CITY_MAX_LENGTH,
  COUNTRY_MAX_LENGTH: FIELD_VALIDATION.COUNTRY_MAX_LENGTH,
  ZIP_MAX_LENGTH: FIELD_VALIDATION.ZIP_MAX_LENGTH,
  TELEPHONE_MAX_LENGTH: FIELD_VALIDATION.TELEPHONE_MAX_LENGTH,
} as const;

// Allowed values for specific fields
export const ALLOWED_VALUES = {
  LANGUAGE_LEVELS: ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const,
  SKILL_LEVELS: ['db.beginner', 'db.intermediate', 'db.advanced', 'db.expert'] as const,
  GENDERS: ['db.male', 'db.female', 'db.other'] as const,
} as const;

// Type guards for validation
export type LanguageLevel = typeof ALLOWED_VALUES.LANGUAGE_LEVELS[number];
export type SkillLevel = typeof ALLOWED_VALUES.SKILL_LEVELS[number];
export type Gender = typeof ALLOWED_VALUES.GENDERS[number];

/**
 * Helper function to check if a value exceeds max length
 */
export function exceedsMaxLength(value: string | null | undefined, maxLength: number): boolean {
  return !!value && value.length > maxLength;
}

/**
 * Helper function to validate required field
 */
export function isRequiredFieldValid(value: string | null | undefined): boolean {
  return !!value && value.trim().length > 0;
}

/**
 * Get validation message translation key for max length errors
 * These keys should be defined in your i18n translation files
 */
export function getMaxLengthErrorKey(fieldName: string): string {
  return `validation.maxLength.${fieldName}`;
}

/**
 * Get validation message translation key for required field errors
 */
export function getRequiredErrorKey(fieldName: string): string {
  return `validation.required.${fieldName}`;
}

// Translation keys for validation messages
export const VALIDATION_ERROR_KEYS = {
  // Required field errors
  REQUIRED: 'validation.required',
  EMPLOYER_REQUIRED: 'validation.required.employer',
  POSITION_REQUIRED: 'validation.required.position',
  START_DATE_REQUIRED: 'validation.required.startDate',
  END_DATE_REQUIRED: 'validation.required.endDate',
  
  // Max length errors
  MAX_LENGTH: 'validation.maxLength',
  EMPLOYER_MAX_LENGTH: 'validation.maxLength.employer',
  POSITION_MAX_LENGTH: 'validation.maxLength.position',
  CITY_MAX_LENGTH: 'validation.maxLength.city',
  COUNTRY_MAX_LENGTH: 'validation.maxLength.country',
  
  // Database constraint errors (critical)
  DATABASE_CONSTRAINT: 'validation.databaseConstraint',
  DATABASE_CONSTRAINT_EMPLOYER: 'validation.databaseConstraint.employer',
  DATABASE_CONSTRAINT_POSITION: 'validation.databaseConstraint.position',
  DATABASE_CONSTRAINT_CITY: 'validation.databaseConstraint.city',
  DATABASE_CONSTRAINT_COUNTRY: 'validation.databaseConstraint.country',
} as const;